import { getApiBaseUrl, httpWithAuth } from './apiClient';
import { getEntraAccessToken } from './authService';
const API_BASE_URL = `${getApiBaseUrl()}/api`;

export interface ChatMessage {
  id: number;
  type: 'system' | 'user' | 'assistant';
  text: string;
  timestamp?: string;
  slideSuggestion?: SlideSuggestion;
  presentation_id?: string;
}

export interface SlideSuggestion {
  suggested: boolean;
  slideType?: string;
  confidence?: number;
  reason?: string;
}

export interface BusinessContext {
  industry?: string;
  company_type?: string;
  presentation_goal?: string;
  templateType?: string;
  templateName?: string;
  category?: string;
}


export interface ThreadListItem {
  id: string;
  title?: string;
  last_message?: string;
  message_count: number;
  created_at: string;
  updated_at?: string;
}

export interface ThreadListResponse {
  threads: ThreadListItem[];
  total: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface ThreadMessage {
  id: string;
  type: string;
  text: string;
  created_at: string;
  presentation_id?: string;
}

export interface ThreadDetailResponse {
  id: string;
  title?: string;
  messages: ThreadMessage[];
  message_count: number;
  created_at: string;
  updated_at?: string;
  is_presentation_generated: boolean;
}

export interface AgentArtifact {
  key: string;
  node: string;
  title: string;
  description: string;
  summary?: string;
  data?: any;
}

export interface ThreadArtifactsResponse {
  thread_id: string;
  artifacts: AgentArtifact[];
}

export interface StreamCallbacks {
  onToken?: (token: string) => void;
  onMessage?: (message: { type: string; content: string; [key: string]: any }) => void;
  onNodeComplete?: (nodeName: string) => void;
  onArtifactAvailable?: (artifact: AgentArtifact) => void;
  onPresentationComplete?: () => void;
  onError?: (error: string) => void;
  onDone?: () => void;
}


export class ChatService {
  private baseUrl = API_BASE_URL;

  // Create a new thread with initial message
  async createThread(message: string, title?: string): Promise<{ thread_id: string; messages: Array<{ role: string; content: string }>; is_presentation_generated: boolean } | null> {
    try {
      // Step 1: Create thread
      const threadResponse = await httpWithAuth<{ id: string | { toString(): string }; title?: string; last_message?: string; message_count: number; created_at: string; updated_at?: string }>(`${this.baseUrl}/chat/threads`, {
        method: 'POST',
        body: JSON.stringify({
          title: title || undefined
        }),
      });

      const threadId = typeof threadResponse.id === 'string' ? threadResponse.id : String(threadResponse.id);

      // Step 2: Send initial message using invoke
      const invokeResponse = await httpWithAuth<any>(`${this.baseUrl}/chat/threads/${threadId}/invoke`, {
        method: 'POST',
        body: JSON.stringify({
          message
        }),
      });

      // Convert LangGraph state to expected format
      // invoke response is the final state from LangGraph
      // Handle both direct state and stream_mode response formats
      let state;
      if (Array.isArray(invokeResponse)) {
        // Response is array format: [["values", {...}], ["updates", {...}], ...]
        // Get the last "values" entry
        const lastValuesEntry = invokeResponse.filter(([key]: [string, any]) => key === 'values').pop();
        state = lastValuesEntry ? lastValuesEntry[1] : {};
      } else {
        state = invokeResponse.values || invokeResponse;
      }

      const allMessages = state.messages || [];

      // Filter out user message (type: 'human') to avoid duplication
      // Only return AI responses since user message is already displayed in frontend
      const convertedMessages = allMessages
        .filter((msg: any) => {
          const msgType = msg.type || msg.role || '';
          // Include AI messages and tool messages, exclude human messages
          return msgType === 'ai' || msgType === 'tool';
        })
        .map((msg: any) => {
          const msgType = msg.type || msg.role || 'ai';
          const msgContent = msg.content || msg.text || '';

          let role = msgType;
          if (msgType === 'human') {
            role = 'user';
          } else if (msgType === 'ai') {
            role = 'ai';
          }

          return {
            role,
            content: msgContent
          };
        });

      // Check if presentation was generated
      const is_presentation_generated = !!(state.pptxgenjs_code || state.values?.pptxgenjs_code);

      return {
        thread_id: threadId,
        messages: convertedMessages,
        is_presentation_generated
      };
    } catch (error) {
      console.error('Error creating thread:', error);
      throw error;
    }
  }

  // Send message to an existing thread
  async sendMessageToThread(threadId: string, message: string): Promise<{ thread_id: string; messages: Array<{ role: string; content: string }>; is_presentation_generated: boolean } | null> {
    try {
      const invokeResponse = await httpWithAuth<any>(`${this.baseUrl}/chat/threads/${threadId}/invoke`, {
        method: 'POST',
        body: JSON.stringify({
          message
        }),
      });

      // Convert LangGraph state to expected format
      // Extract only NEW messages from "updates" entries (not full history from "values")
      let newMessages: any[] = [];
      let state: any = {};

      if (Array.isArray(invokeResponse)) {
        // Response is array format: [["values", {...}], ["updates", {...}], ...]

        // Get the last "values" entry for pptxgenjs_code check
        const lastValuesEntry = invokeResponse.filter(([key]: [string, any]) => key === 'values').pop();
        state = lastValuesEntry ? lastValuesEntry[1] : {};

        // Extract new messages from "updates" entries only
        // Each update contains node outputs with messages (e.g., { "clarify_with_user": { "messages": [...] } })
        const updatesEntries = invokeResponse.filter(([key]: [string, any]) => key === 'updates');
        for (const [, updateData] of updatesEntries) {
          for (const nodeOutput of Object.values(updateData || {})) {
            const nodeMessages = (nodeOutput as any)?.messages || [];
            newMessages.push(...nodeMessages);
          }
        }
      } else {
        state = invokeResponse.values || invokeResponse;
        // Fallback: use all messages if not array format
        newMessages = state.messages || [];
      }

      // Filter to only AI messages (exclude human messages we just sent)
      const convertedMessages = newMessages
        .filter((msg: any) => {
          const msgType = msg.type || msg.role || '';
          return msgType === 'ai' || msgType === 'tool';
        })
        .map((msg: any) => {
          const msgType = msg.type || msg.role || 'ai';
          const msgContent = msg.content || msg.text || '';

          let role = msgType;
          if (msgType === 'human') {
            role = 'user';
          } else if (msgType === 'ai') {
            role = 'ai';
          }

          return {
            role,
            content: msgContent
          };
        });

      // Check if presentation was generated
      const is_presentation_generated = !!(state.pptxgenjs_code || state.values?.pptxgenjs_code);

      return {
        thread_id: threadId,
        messages: convertedMessages,
        is_presentation_generated
      };
    } catch (error) {
      console.error('Error sending message to thread:', error);
      throw error;
    }
  }

  // Get thread by ID with complete message history
  async getThreadById(threadId: string): Promise<ThreadDetailResponse | null> {
    try {
      const stateResponse = await httpWithAuth<any>(`${this.baseUrl}/chat/threads/${threadId}`, {
        method: 'GET',
      });

      // Get thread info from database (we need to call search to get thread details)
      const threadsResponse = await httpWithAuth<Array<{ id: string | { toString(): string }; title?: string; last_message?: string; message_count: number; created_at: string; updated_at?: string }>>(`${this.baseUrl}/chat/threads/search`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      const threadInfo = threadsResponse.find(t => {
        const threadIdStr = typeof t.id === 'string' ? t.id : String(t.id);
        return threadIdStr === threadId;
      });
      if (!threadInfo) {
        throw new Error('Thread not found');
      }

      // Convert LangGraph state to ThreadDetailResponse
      // Handle LangGraph StateSnapshot response (array format)
      // [values, tasks, config, metadata, timestamp, parent_config, ...]
      let state;
      if (Array.isArray(stateResponse)) {
        // First element contains the state values with messages
        state = stateResponse[0] || {};
      } else {
        state = stateResponse.values || stateResponse;
      }
      const messages = state.messages || [];

      // Convert messages to ThreadMessage format
      const convertedMessages: ThreadMessage[] = messages.map((msg: any, index: number) => {
        const msgType = msg.type || msg.role || 'ai';
        const msgContent = msg.content || msg.text || '';

        // Map message types for frontend
        let type = msgType;
        if (msgType === 'human') {
          type = 'user';
        } else if (msgType === 'ai') {
          type = 'assistant';
        }

        return {
          id: msg.id || `msg-${index}`,
          type,
          text: msgContent,
          created_at: msg.created_at || new Date().toISOString(),
          presentation_id: undefined
        };
      });

      // Check if presentation was generated
      const is_presentation_generated = !!(state.pptxgenjs_code || state.values?.pptxgenjs_code);

      return {
        id: threadId,
        title: threadInfo.title,
        messages: convertedMessages,
        message_count: convertedMessages.length,
        created_at: threadInfo.created_at,
        updated_at: threadInfo.updated_at,
        is_presentation_generated
      };
    } catch (error) {
      console.error('Error getting thread:', error);
      throw error;
    }
  }

  // Get user's threads (new backend implementation)
  async getThreads(page: number = 1, perPage: number = 20): Promise<ThreadListResponse | null> {
    try {
      const threads = await httpWithAuth<Array<{ id: string | { toString(): string }; title?: string; last_message?: string; message_count: number; created_at: string; updated_at?: string }>>(`${this.baseUrl}/chat/threads/search`, {
        method: 'POST',
        body: JSON.stringify({}),
      });

      // Convert to ThreadListResponse format with pagination
      const total = threads.length;
      const startIndex = (page - 1) * perPage;
      const endIndex = startIndex + perPage;
      const paginatedThreads = threads.slice(startIndex, endIndex);

      const threadListItems: ThreadListItem[] = paginatedThreads.map(thread => ({
        id: typeof thread.id === 'string' ? thread.id : String(thread.id),
        title: thread.title,
        last_message: thread.last_message,
        message_count: thread.message_count,
        created_at: thread.created_at,
        updated_at: thread.updated_at
      }));

      return {
        threads: threadListItems,
        total,
        page,
        per_page: perPage,
        has_next: endIndex < total,
        has_prev: page > 1
      };
    } catch (error) {
      console.error('Error getting threads:', error);
      throw error;
    }
  }

  async getArtifacts(threadId: string): Promise<ThreadArtifactsResponse> {
    return httpWithAuth<ThreadArtifactsResponse>(`${this.baseUrl}/chat/threads/${threadId}/artifacts`, {
      method: 'GET',
    });
  }

  async getVisualReviewImageUrl(threadId: string, slideNumber: number, attempt?: number): Promise<string> {
    const token = await getEntraAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const path = attempt
      ? `visual_review/history/${attempt}/images/${slideNumber}`
      : `visual_review/images/${slideNumber}`;

    const response = await fetch(
      `${this.baseUrl}/chat/threads/${threadId}/artifacts/${path}`,
      { method: 'GET', headers },
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch artifact image: ${response.status}`);
    }
    const blob = await response.blob();
    if (!blob.type.startsWith('image/')) {
      throw new Error(`Failed to fetch artifact image: expected image, got ${blob.type || 'unknown content type'}`);
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read artifact image'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read artifact image'));
      reader.readAsDataURL(blob);
    });
  }

  // Download presentation by ID
  async downloadPresentation(presentationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const headers: Record<string, string> = {};

      // Use Entra token
      const token = await getEntraAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${this.baseUrl}/presentations/${presentationId}/download`, {
        method: 'GET',
        headers,
      });

      // Check for errors (401 will be handled by global handler via error throw)
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        if (response.status === 403) {
          throw new Error('Access denied. You can only download your own presentations.');
        }
        if (response.status === 404) {
          throw new Error('Presentation not found.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="([^"]+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `presentation_${presentationId.slice(0, 8)}.pptx`;

      // Download file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading presentation:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to download presentation'
      };
    }
  }

  // Stream from a specific endpoint (SSE)
  private async streamFromEndpoint(url: string, message: string, callbacks: StreamCallbacks): Promise<void> {
    const token = await getEntraAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('UNAUTHORIZED');
      if (response.status === 429) throw new Error('429: Rate limit reached');
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            switch (data.type) {
              case 'token':
                callbacks.onToken?.(data.content);
                break;
              case 'message':
                callbacks.onMessage?.(data.message);
                break;
              case 'node_complete':
                callbacks.onNodeComplete?.(data.node);
                break;
              case 'artifact_available':
                callbacks.onArtifactAvailable?.(data.artifact);
                break;
              case 'presentation_complete':
                callbacks.onPresentationComplete?.();
                break;
              case 'error':
                callbacks.onError?.(data.message || data.content || 'Stream error');
                break;
              case 'done':
                callbacks.onDone?.();
                break;
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }
    }
  }

  // Create a new thread and stream the response
  async createThreadStream(message: string, title: string | undefined, callbacks: StreamCallbacks): Promise<string> {
    const threadResponse = await httpWithAuth<{ id: string | { toString(): string } }>(`${this.baseUrl}/chat/threads`, {
      method: 'POST',
      body: JSON.stringify({ title: title || undefined }),
    });
    const threadId = typeof threadResponse.id === 'string' ? threadResponse.id : String(threadResponse.id);

    await this.streamFromEndpoint(`${this.baseUrl}/chat/threads/${threadId}/stream`, message, callbacks);

    return threadId;
  }

  // Send message to an existing thread and stream the response
  async sendMessageToThreadStream(threadId: string, message: string, callbacks: StreamCallbacks): Promise<void> {
    await this.streamFromEndpoint(`${this.baseUrl}/chat/threads/${threadId}/stream`, message, callbacks);
  }

  // Delete a thread
  async deleteThread(threadId: string): Promise<void> {
    await httpWithAuth(`${this.baseUrl}/chat/threads/${threadId}`, { method: 'DELETE' });
  }

  // Rename a thread
  async renameThread(threadId: string, title: string): Promise<void> {
    await httpWithAuth(`${this.baseUrl}/chat/threads/${threadId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    });
  }

}

export const chatService = new ChatService();
export default chatService;
